import { useEffect, useMemo, useState } from 'react';
import {
  fetchLookupData,
  getDocument,
  getSurfaces,
} from '../util/docPreviewUtils';

/**
 * A hook that generates a list of preview URLs (one URL for each surface on the doc)
 * for both the original and proposed doc ref urls on a design feedback request.
 *
 * @param {*} request - a design feedback request in the following shape:
 *
 * {
 *    originalDocumentReferenceUrl: 'og-doc.com',
 *    proposedOptions: [
 *       {
 *          optionId: 'option1'
 *          updatedDocumentReferenceUrl: 'option1-doc.com'
 *       }
 *    ]
 * }
 *
 * @returns - proposedDesignOptions, originalDesign, designsAreReady
 *
 * proposeDesignOptions:
 * [
 *    {
 *       optionId: 'option1'
 *       updatedDocumentReferenceUrl: 'option1-doc.com'
 *       previews: [
 *          'option1-surface1.com',
 *          'option1-surface2.com'
 *       ]
 *    }
 * ]
 *
 * originalDesign:
 * {
 *    originalDocumentReferenceUrl: 'og-doc.com',
 *    previews: [
 *      'og-surface1.com',
 *      'og-surface2.com'
 *    ]
 * }
 *
 * designsAreReady: boolean
 */
export const useDocPreviewsGenerator = (request, lineItem) => {
  /**
   * The list of proposed design options on the request
   *
   * Example:
   * [
   *  {
   *    optionId: 'option1'
   *    updatedDocumentReferenceUrl: 'option1-doc.com'
   *   }
   * ]
   */
  const proposedDesignOptionsWithoutPreviews = useMemo(
    () => request?.proposedOptions || [],
    [request?.proposedOptions]
  );

  /**
   * The list of proposed design options on the request that will be updated with
   * a new `previews` field with its list of generated preview URLs (one URL for each surface on the doc)
   *
   * Example:
   * [
   *    {
   *       optionId: 'option1'
   *       updatedDocumentReferenceUrl: 'option1-doc.com'
   *       previews: [
   *          'option1-surface1.com',
   *          'option1-surface2.com'
   *       ]
   *    }
   * ]
   */
  const [
    proposedDesignOptionsWithPreviews,
    setProposedDesignOptionsWithPreviews,
  ] = useState(proposedDesignOptionsWithoutPreviews);

  /**
   * The original design doc ref URL on the request
   */
  const originalDocumentReferenceUrl =
    request?.originalDocumentReferenceUrl || '';

  /**
   * An object containing the doc ref url of the original design,
   * along with a new `previews` field with its list of generated preview URLs (one URL for each surface on the doc)
   *
   * Example:
   * {
   *    originalDocumentReferenceUrl: 'og-doc.com',
   *    previews: [
   *      'og-surface1.com',
   *      'og-surface2.com'
   *    ]
   * }
   */
  const [originalDesignWithPreviews, setOriginalDesignWithPreviews] = useState(
    {}
  );

  const [previewLookupData, setPreviewLookupData] = useState({
    designPreview: {},
    productPreview: {},
  });
  const [previewError, setPreviewError] = useState(false);

  useEffect(() => {
    async function fetchLookupPreviewData() {
      try {
        const previewDataFromMcp = await fetchLookupData(
          lineItem.productKey,
          lineItem.productVersion,
          lineItem.displayProductAttributes
        );

        // after we get the lookup data, we filter what we need
        // designPreview - show design
        // productPreview - show design on product
        const designPreview = previewDataFromMcp.filter(
          lookupData =>
            lookupData.purpose.toLowerCase() === 'design' &&
            lookupData.subPurpose.toLowerCase() === 'panel'
        )[0];
        const productPreview = previewDataFromMcp.filter(
          lookupData =>
            lookupData.purpose.toLowerCase() === 'product' &&
            lookupData.subPurpose.toLowerCase() === 'full'
        )[0];

        const previewData = {
          designPreview,
          productPreview,
        };
        setPreviewLookupData(previewData);
      } catch (err) {
        setPreviewError(true);
      }
    }

    fetchLookupPreviewData();
  }, [
    lineItem.productKey,
    lineItem.productVersion,
    lineItem.displayProductAttributes,
  ]);

  const [cimDoc, setCimDoc] = useState('');

  // Generate and persist the list of preview URLs for each proposed design option
  useEffect(() => {
    const generateDesignOptionWithPreviews = async (index, docRefUrl) => {
      const previews = await getSurfaces(docRefUrl);
      setProposedDesignOptionsWithPreviews(prevData => {
        const updatedDesigns = [...prevData];
        updatedDesigns[index] = { ...prevData[index], previews };
        return updatedDesigns;
      });

      try {
        const documents = await getDocument(docRefUrl);
        setCimDoc(documents.cimDocUrl);
      } catch (err) {
        setPreviewError(true);
      }
    };

    proposedDesignOptionsWithoutPreviews.forEach(async (option, index) => {
      generateDesignOptionWithPreviews(
        index,
        option.updatedDocumentReferenceUrl
      );
    });
  }, [proposedDesignOptionsWithoutPreviews]);

  // Generate and persist the list of preview URLs for the original design
  useEffect(() => {
    const generateOriginalDesignWithPreviews = async docRefUrl => {
      const previews = await getSurfaces(docRefUrl);
      setOriginalDesignWithPreviews({ originalDocumentReferenceUrl, previews });
    };
    generateOriginalDesignWithPreviews(originalDocumentReferenceUrl);
  }, [originalDocumentReferenceUrl]);

  // Once the async calls to generate the previews have resolved, set the ready flag as `true`
  const previewsAreReady =
    proposedDesignOptionsWithPreviews?.every(option => option?.previews) &&
    originalDesignWithPreviews?.previews &&
    cimDoc &&
    JSON.stringify(previewLookupData.designPreview) !== '{}' &&
    JSON.stringify(previewLookupData.productPreview) !== '{}';

  return {
    proposedDesignOptions: proposedDesignOptionsWithPreviews,
    originalDesign: originalDesignWithPreviews,
    designsAreReady: previewsAreReady,
    previewError,
    previewLookupData,
    cimDoc,
  };
};
